import { defineStore } from 'pinia';
import { getDatabase, ref, onValue } from 'firebase/database';
import app from '@/firebase';
import api from '@/js/api.js';
import { updatePreset } from '@primevue/themes';
import convert from 'color-convert';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';

// Define the store
export const useDonorStore = defineStore('donorStore', {
  state: () => ({
    donations: [],
    filteredDonations: [],
    donorInfo: {},
    totalAmountDonated: 0,
    totalDonations: 0,
    totalCauses: 0,
    staticTotalAmountDonated: 0,
    staticTotalDonations: 0,
    staticTotalCauses: 0,
    donorOrganizations: [],
    categoryAmounts: [],
    organizationAmounts: [],
    hostsDynamicParams: {},
    hostPreferences: {
      categories: null,
      orgSizes: null,
      areasOfImpact: null,
    },
    donorPreferences: {
      categories: [],
      orgSizes: [],
      areasOfImpact: [],
    },
    states: [],
    stateCode: '',
    countries: [],
    mainColor: '',
    featureCampaigns: false,
    featureRecurringDonations: false,
    featureCompanyGiving: false,
    lighterColor: '',
    darkerColor: '',
    colors: [],
    lighterColor: '',
    darkerColor: '',
    formId: '',
    domain: '',
    hosts_id: null,
    isDataLoaded: false,
    donorAccounts_id: null,
    errorDisplay: false,
    errors: {},
    testMode: false, // this test mode variable is used to trigger the welcome message from cypress
    newUser: false,
    sent: false,
    processingReceipt: false,
    paymentProfiles: [],
    showDeactivateAccount: false,
    showDeleteRecurringPaymentMethod: false,
    showDeactivatePaymentMethod: false,
    showDeactivateRecurringDonation: false,
    errorOccurred: false,
    errorMessage: '',
    activeRecurring: [],
    inactiveRecurring: [],
    activeRecurringLoading: false,
    recurringPaymentProfiles: [],
    fundraisers: [],
    fundraiserDonationData: {},
    isLoading: false,
    hostLogo: '',
    hasHostLogo: false,
    fundraisersLoading: false,
    skipOnAuthenticated: false,
    filterSelections: {
      status: '',
      amountSort: '',
      goalSort: '',
      nameSort: '',
    },
  }),

  actions: {
    setNewUser(value) {
      this.newUser = value;
    },
    async onAuthenticated() {
      if (this.skipOnAuthenticated) return;

      // Waiting for Required Data to be fetched
      await this.fetchDonationsAndDonor();
      if (useAuthStore().type == 'companyAccount') {
        useCompanyAdminStore().getCompany();
      }

      this.fetchFundraisers();
      //TODO: Need to move this fetchFundraisers to not run on Authenticated
      // - Used in Fundraisers.vue, Overview.vue, Possible ActiveFundraisers.vue, Potentially can be moved to FundraiserStore.js ?
      // Also used in Donor Store at the following locations:
      // toggleFundraiserStatus
      // FundraiserDetails.vue
      // getFundraiserByID - FundraiserDetails, Step5, Left Column, RightColumn, ShareModal
    },
    setPrimeVueTheme() {
      const rgb = convert.hex.rgb(this.mainColor);
      const hsl = convert.rgb.hsl(rgb);
      updatePreset({
        semantic: {
          primary: {
            50: `hsl(${hsl[0]}, ${hsl[1]}%, 95%)`,
            100: `hsl(${hsl[0]}, ${hsl[1]}%, 90%)`,
            200: `hsl(${hsl[0]}, ${hsl[1]}%, 80%)`,
            300: `hsl(${hsl[0]}, ${hsl[1]}%, 70%)`,
            400: `hsl(${hsl[0]}, ${hsl[1]}%, 60%)`,
            500: `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`,
            600: `hsl(${hsl[0]}, ${hsl[1]}%, 40%)`,
            700: `hsl(${hsl[0]}, ${hsl[1]}%, 30%)`,
            800: `hsl(${hsl[0]}, ${hsl[1]}%, 20%)`,
            900: `hsl(${hsl[0]}, ${hsl[1]}%, 10%)`,
            950: `hsl(${hsl[0]}, ${hsl[1]}%, 5%)`,
          },
        },
      });
    },
    async fetchHostFromPortalDomain() {
      try {
        let host = window.location.host;
        if (window.location.hostname == 'localhost') {
          host = useAuthStore().localhostPortalDomain;
        }
        const response = await api.get(`hosts/cms-settings?portalDomain=https://${host}/`);
        this.hosts_id = response.data.data.hosts_id;
        this.hostsDynamicParams = response.data.data.hostsDynamicParams;
        this.givesDaysId = response.data.data.givesDay?.givesDays_id;
        this.hostLogo =
          'https://cdninternal.civicore.com/uploadedFiles/giving_affiliate/hosts/hostLogo/' + this.hosts_id;
        this.hostName = response.data.data.host.name;
        this.mainColor = response.data.data.host.mainColor;
        this.hostUrl = response.data.data.host.domain;
        this.setPrimeVueTheme();
      } catch (error) {
        console.error('Failed to fetch host from portal domain:', error);
        this.hosts_id = null;
      }
    },
    async fetchDonationsAndDonor() {
      try {
        const response = await api.get(`/donor-account`);
        this.donorInfo = response.data.data.donorAccountInfo;
        this.donorAccounts_id = response.data.data.donorAccountInfo.donorAccounts_id;
        this.donations = response.data.data.donations;
        this.totalAmountDonated = response.data.data.totalAmountDonated;
        this.totalDonations = response.data.data.totalDonations;
        this.donations.forEach((donation) => {
          const organization = donation.organization;
          this.donorOrganizations.push(organization);
        });
        this.staticTotalAmountDonated = response.data.data.totalAmountDonated;
        this.staticTotalDonations = response.data.data.totalDonations;
        this.staticTotalCauses = response.data.data.totalCauses;
        this.categoryAmounts = response.data.data.categoryAmounts;
        this.organizationAmounts = response.data.data.organizationAmounts;
        this.mainColor = response.data.data.hostsInfo.mainColor;
        this.setPrimeVueTheme();
        this.featureCampaigns = response.data.data.hostsInfo.feature_campaigns == 1;
        this.featureRecurringDonations = response.data.data.hostsInfo.feature_recurringDonations == 1;
        this.featureCompanyGiving = response.data.data.hostsInfo.loq_companyGiving == 1;
        localStorage.setItem('mainColor', this.mainColor);
        this.hosts_id = response.data.data.hostsInfo.hosts_id;
        this.hostUrl = response.data.data.hostsInfo.domain;
        this.hasHostLogo = response.data.data.hostsInfo.hostLogo ? true : false;
        this.hostLogo =
          'https://cdninternal.civicore.com/uploadedFiles/giving_affiliate/hosts/hostLogo/' + this.hosts_id;
        this.domain =
          response.data.data.hostsInfo.domain == 'https://yourcommunityfoundation.civicore.com'
            ? 'https://uat-demo-for-product-development.multiscreensite.com'
            : response.data.data.hostsInfo.domain.replace(/\/$/, '');

        this.formId = response.data.data.hostsInfo.form_id;
        this.hostsDynamicParams = response.data.data.hostsDynamicParams;
        this.isDataLoaded = true;

        // Parse preferences JSON string
        const preferences =
          typeof this.donorInfo.preferences === 'string'
            ? JSON.parse(this.donorInfo.preferences)
            : this.donorInfo.preferences || {};

        // Assign arrays from preferences to store properties
        if (preferences) {
          this.donorPreferences.categories = preferences.categories ? preferences.categories : [];
          this.donorPreferences.areasOfImpact = preferences.areasOfImpact ? preferences.areasOfImpact : [];
          this.donorPreferences.orgSizes = preferences.orgSizes ? preferences.orgSizes : [];
        }
      } catch (error) {
        console.error('Failed to fetch donations and donor:', error);
        this.donorInfo = {}; // Change this line to set an empty object
        this.donations = [];
      }
    },
    async fetchDonorPaymentProfiles() {
      try {
        const response = await api.get(`/donor-account/payment-profiles?donorAccounts_id=${this.donorAccounts_id}`);
        this.paymentProfiles = response.data.data.paymentProfiles;
      } catch (error) {
        console.error('Failed to fetch Donor Payment Profiles. Error: ', error.message);
      }
    },
    async getSingleReceipt(transactionsId) {
      const url = `/single-receipt?hosts_id=${this.donorInfo.hosts_id}&donorAccounts_id=${this.donorAccounts_id}&transactions_id=${transactionsId}`;
      try {
        this.processingReceipt = true;
        const response = await api.get(url, { mode: 'cors' });
        this.axiosRequest = null;
        const data = response.data;
        this.processingReceipt = false;
        this.errorDisplay = false;
        this.sent = true;
        return data;
      } catch (error) {
        this.processingReceipt = false;
        this.errorDisplay = true;
        this.sent = false;
        if (error.message.includes('Error retrieving credentials from the instance profile metadata service')) {
          console.error('AWS credentials error:', error.message);
        } else {
          console.error('Failed to get single receipt:', error.message);
        }
        return null;
      }
    },
    async updateDonorAccount(id, updatedFields) {
      try {
        const response = await api.put(`/donor-account/${id}`, updatedFields);
        // Update the store's state with the updated donor info
        this.donorInfo = {
          ...this.donorInfo,
          ...response.data.data,
        };
      } catch (error) {
        console.error('Failed to update donor account:', error);
      }
    },
    async fetchHostPreferences() {
      try {
        const hostId = this.hosts_id;
        const [categoriesResponse, areasOfImpactResponse, orgSizesResponse] = await Promise.all([
          api.get(`/organization-preferences?hosts_id=${hostId}&customFields_id=1`),
          api.get(`/organization-preferences?hosts_id=${hostId}&customFields_id=2`),
          api.get(`/organization-preferences?hosts_id=${hostId}&customFields_id=4`),
        ]);
        this.hostPreferences.categories = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data.map((item) => ({
              longValue: item.longValue,
              customLookups_id: item.customLookups_id,
            }))
          : [];
        this.hostPreferences.areasOfImpact = Array.isArray(areasOfImpactResponse.data)
          ? areasOfImpactResponse.data.map((item) => ({
              longValue: item.longValue,
              customLookups_id: item.customLookups_id,
            }))
          : [];
        this.hostPreferences.orgSizes = Array.isArray(orgSizesResponse.data)
          ? orgSizesResponse.data.map((item) => ({
              longValue: item.longValue,
              customLookups_id: item.customLookups_id,
            }))
          : [];
      } catch (error) {
        console.error('Failed to retrieve preferences', error);
        this.hostPreferences.categories = null;
        this.hostPreferences.areasOfImpact = null;
        this.hostPreferences.orgSizes = null;
      }
    },
    async fetchStateCountry() {
      try {
        const [stateResponse, countryResponse] = await Promise.all([
          api.get(`/donor-states?fwLookup_id=1`),
          api.get(`/donor-states?fwLookup_id=129`),
        ]);

        const formattedStates = Array.isArray(stateResponse.data)
          ? stateResponse.data
              .map((item) => ({
                name: item.longValue,
                shortName: item.shortValue,
                value: item.lookup_value_id,
              }))
              .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
          : [];

        const formattedCountries = Array.isArray(countryResponse.data)
          ? countryResponse.data.map((item) => ({
              name: item.longValue,
              value: item.lookup_value_id,
            }))
          : [];

        // Set the formatted options to states and countries
        this.states = formattedStates;
        this.countries = formattedCountries;
      } catch (error) {
        console.error('Failed to retrieve state and country data', error);
        this.states = [];
        this.countries = [];
      }
    },
    async deactivateAccount() {
      try {
        const response = await api.put(`/donor-account/${this.donorInfo.donorAccounts_id}`, {
          active: false,
        });
        this.donorInfo.active = false;
        this.errorOccurred = false;
        this.errorMessage = '';
        this.showDeactivateAccount = false;
      } catch (error) {
        console.error('Failed to deactivate account:', error);
        this.errorOccurred = true;
        this.errorMessage = 'Failed to deactivate account. Please try again later.';
      }
    },
    async updateDonorAccountPassword(updatedFields) {
      try {
        const { currentPassword, newPassword } = updatedFields;
        let payload = {
          currentPassword,
          newPassword,
          hosts_id: this.donorInfo.hosts_id,
          donorAccounts_id: this.donorInfo.donorAccounts_id,
        };

        const response = await api.post('/update-password', payload);
        if (response.data.code === 200) {
          return true;
        } else {
          return response.message;
        }
      } catch (error) {
        console.error('Failed to update donor account:', error);
        throw error;
      }
    },
    async updateStateCode() {
      const selectedState = this.states.find((state) => state.value === this.donorInfo.state);
      this.stateCode = selectedState ? selectedState.shortName : null;
    },
    async updateRecurringTransactions(updatedRecurringTransactions) {
      try {
        const response = await api.post(`/update-payment-recurring-donation`, {
          recurringTransactions: updatedRecurringTransactions,
        });
        this.donorInfo.recurringTransactions = response.data.recurringTransactions;
        return response.data; // Return the response data
      } catch (error) {
        console.error('Failed to update recurring transactions:', error);
        throw error; // Rethrow the error
      }
    },
    async addPaymentMethod(paymentMethod) {
      try {
        paymentMethod = JSON.parse(paymentMethod); // Parse the JSON string into an object
        let requestData = {
          paymentProfileName: paymentMethod.paymentProfileName,
          donorAccounts_id: this.donorInfo.donorAccounts_id,
          token: paymentMethod.token,
          type: paymentMethod.type,
        };
        if (paymentMethod.type == 'cc') {
          requestData.expiration = paymentMethod.expiration;
          requestData.last4 = paymentMethod.last4;
          requestData.brand = paymentMethod.brand;
          requestData.zipCode = paymentMethod.zipCode;
        }

        // Send the API request with the requestData object
        const response = await api.post('/payment-profile', requestData);
        // Add hasRecurringDonation property later
        if (response.data.code === 200) {
          const newPayment = response.data.data;
          newPayment.hasRecurringDonation = false;
          newPayment.recurringDonationId = [];
          this.paymentProfiles.push(newPayment);
        }
      } catch (error) {
        console.error('Failed to add payment method:', error);
        throw error;
      }
    },
    async deactivatePaymentMethod(paymentMethodId) {
      try {
        const response = await api.post(`/payment-profile/${paymentMethodId}`);
        this.errorOccurred = false;
        this.errorMessage = '';
        this.showDeactivatePaymentMethod = false;
        return true;
      } catch (error) {
        console.error('Failed to delete payment method:', error);
        this.errorOccurred = true;
        this.errorMessage = 'Failed to delete payment method. Please try again later.';
        throw error;
      }
    },
    async updateRecurringStatus(recurringId, status) {
      const response = await api.put(`update-recurring-status/${recurringId}`, {
        inactive: status,
      });

      // Find the donation in activeRecurring
      const activeIndex = this.activeRecurring.findIndex((donation) => donation.recurringDonations_id === recurringId);

      // Find the donation in inactiveRecurring
      const inactiveIndex = this.inactiveRecurring.findIndex(
        (donation) => donation.recurringDonations_id === recurringId,
      );

      // If the donation was found in activeRecurring and the new status is inactive, move it to inactiveRecurring
      if (activeIndex !== -1 && status === 1) {
        const [donation] = this.activeRecurring.splice(activeIndex, 1);
        donation.inactive = response.data.data.inactive;
        this.inactiveRecurring.push(donation);
      }

      // If the donation was found in inactiveRecurring and the new status is active, move it to activeRecurring
      if (inactiveIndex !== -1 && status === 0) {
        const [donation] = this.inactiveRecurring.splice(inactiveIndex, 1);
        donation.inactive = response.data.data.inactive;
        this.activeRecurring.push(donation);
      }
    },
    async updateRecurringPaymentMethod(data) {
      let recurringDonationData = data;
      if (data.newPayment) {
        recurringDonationData = data.payment;
        await this.addPaymentMethod(data);
        recurringDonationData.paymentProfileId =
          this.paymentProfiles[this.paymentProfiles.length - 1].donorAccounts_paymentProfiles_id;
      }
      try {
        const response = await api.post('/update-recurring-donation', recurringDonationData);
        // Update activeRecurring with the new values
        const recurringId = recurringDonationData.recurringId;
        const index = this.activeRecurring.findIndex((donation) => donation.recurringDonations_id === recurringId);
        if (index !== -1 && response.data.data) {
          const [donation] = this.activeRecurring.splice(index, 1);
          donation.donorAccounts_paymentProfiles_id = response.data.data.donorAccounts_paymentProfiles_id;
          donation.amount_manual = response.data.data.amount_manual;
          if (donation.recurEvery !== response.data.data.recurEvery) {
            donation.recurEvery = response.data.data.recurEvery;
            donation.recurEveryValue =
              response.data.data.recurEvery == '914'
                ? 'Weekly'
                : response.data.data.recurEvery == '915'
                ? 'Monthly'
                : response.data.data.recurEvery == '916'
                ? 'Quarterly'
                : 'Yearly';
          }
          this.activeRecurring.splice(index, 0, donation);
        }
        return response.data;
      } catch (error) {
        console.error('Failed to update recurring payment method:', error);
        throw error;
      }
    },
    async fetchRecurringDonations() {
      const donorId = this.donorAccounts_id;
      if (!donorId) {
        console.error('donorAccounts_id is null');
        return;
      }
      this.activeRecurringLoading = true;
      try {
        const response = await api.get(`recurring-donations?donorAccounts_id=${donorId}`);

        let recurringDonations = [];
        if (response.data && response.data.data && response.data.data.recurringDonations) {
          recurringDonations = response.data.data.recurringDonations;
        } else if (response.data && response.data.recurringDonations) {
          recurringDonations = response.data.recurringDonations;
        } else {
          console.error('Invalid response structure:', response.data);
          return;
        }

        this.activeRecurring = recurringDonations.filter((donation) => !donation.inactive);
        this.inactiveRecurring = recurringDonations.filter((donation) => donation.inactive);
      } catch (error) {
        console.error('Failed to fetch recurring donations:', error);
      }
      this.activeRecurringLoading = false;
    },
    // @todo merge with existing code in FundraiserStore
    async fetchFundraisers() {
      this.fundraisers = [];
      this.fundraisersLoading = true;
      const donorId = this.donorAccounts_id;
      try {
        const response = await api.get(`/retrieve-fundraisers`);
        // Filter out fundraisers with status "Submitted", "Approved - Future", or "Declined"
        // Set using Vue setter for array safety
        this.fundraisers = response.data.data
          .filter(
            (fundraiser) =>
              fundraiser.status !== 'Submitted' &&
              fundraiser.status !== 'Approved - Future' &&
              fundraiser.status !== 'Declined',
          )
          .sort((a, b) => {
            // Sort to have 'Published' status first
            if (a.status === 'Published' && b.status !== 'Published') {
              return -1;
            } else if (a.status !== 'Published' && b.status === 'Published') {
              return 1;
            }
            return 0;
          });
      } catch (error) {
        console.error('Failed to fetch fundraisers:', error);
        this.fundraisers = [];
      }
      this.fundraisersLoading = false;
    },
    async getFundraiserDonationData(givesDaysId) {
      try {
        const db = getDatabase(app);
        const totalAmount = ref(db, `giving_affiliate/${givesDaysId}/campaigns`);
        onValue(
          totalAmount,
          (snapshot) => {
            const data = snapshot.val();
            this.fundraiserDonationData = {
              ...this.fundraiserDonationData,
              ...data,
            };
          },
          (error) => {
            console.error('Error callback called:', error); // Log error callback
            this.fundraiserDonationData = {};
          },
        );
      } catch (error) {
        console.error('Catch block error:', error); // Log catch block error
        this.fundraiserDonationData = {};
      }
    },
    setLoading(status) {
      this.isLoading = status;
    },
    async toggleFundraiserStatus(fundraiserId, action) {
      this.setLoading(true); // Start loading
      const donorId = this.donorAccounts_id;
      try {
        // Include the action ('publish' or 'unpublish') in the request body or as a query parameter
        const response = await api.post(`/toggle-fundraiser-status`, {
          donorAccounts_id: donorId,
          fundraisers_id: fundraiserId,
          action: action, // 'activate' or 'deactivate'
        });
        const fundraiserIndex = this.fundraisers.findIndex((f) => f.fundraisers_id === fundraiserId);
        if (fundraiserIndex !== -1) {
          if (action === 'publish') {
            this.fundraisers[fundraiserIndex].status = 'Published';
          } else if (action === 'unpublish') {
            this.fundraisers[fundraiserIndex].status = 'Deactivated'; // or any other status you use for unpublished fundraisers
          }
        }
      } catch (error) {
        console.error(`Failed to ${action} fundraiser:`, error);
      } finally {
        this.setLoading(false); // Stop loading regardless of the outcome
      }
    },
    generateColors(numColors = 3) {
      let colors = [];
      let mainColor = this.mainColor;

      // Ensure the color is in the correct format (e.g., #RRGGBB)
      if (mainColor.length !== 7 || mainColor[0] !== '#') {
        throw new Error('Invalid mainColor format. Please use the format #RRGGBB.');
      }

      // Define the transparency levels
      //there are only 5 colors needed for the chart
      const transparencyLevels = ['FF', 'CC', '99', '66', '33'];

      // Add the main color without transparency
      colors.push(mainColor);

      // Add the lighter colors with transparency
      for (let i = 1; i < Math.min(numColors, transparencyLevels.length); i++) {
        colors.push(`${mainColor}${transparencyLevels[i]}`);
      }

      // Store the colors in the state
      this.mainColor = mainColor;
      this.lighterColor = mainColor + '66';
      this.lightestColor = mainColor + '33';

      return colors;
    },
    async checkForMainColor() {
      let mainColor = localStorage.getItem('mainColor');
      if (mainColor) {
        this.mainColor = mainColor;
      } else {
        try {
          const data = await api.post('/host-settings', {
            portalDomain: `${window.location.origin}`,
          });
          this.mainColor = data.data.data.hostsInfo.mainColor;
          localStorage.setItem('mainColor', this.mainColor);
        } catch (error) {
          console.error('Error fetching main color:', error);
          this.mainColor = ''; // Set mainColor to an empty string on error
        }
      }
    },
  },

  getters: {
    oneTimeDonations() {
      return this.donations.filter((donation) => donation.recurringDonation === false);
    },
    recurringDonations() {
      return this.donations.filter((donation) => donation.recurringDonation === true);
    },
    filteredOrganizations() {
      return (searchTerm) => {
        return this.donorOrganizations.filter((org) =>
          org.organizationName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      };
    },
    selectedStateShortName() {
      const selectedState = this.states.find((state) => state.value === this.donorInfo.state);
      return selectedState ? selectedState.shortName : null;
    },
    donorFullName() {
      return `${this.donorInfo.firstName ?? ''} ${this.donorInfo.lastName ?? ''}`.trim();
    },
    getFundraiserById: (state) => (id) => {
      const fundraiser = state.fundraisers.find((fundraiser) => fundraiser.fundraisers_id === id);
      if (fundraiser && state.featureCompanyGiving && fundraiser.companyGiving_id) {
        fundraiser.companyGiving = useCompanyAdminStore().companies.find(
          (company) => company.companyGiving_id == fundraiser.companyGiving_id,
        );
      }
      return fundraiser;
    },
    publishedFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'published');
    },
    createdFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'created');
    },
    submittedFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'submitted');
    },
    declinedFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'declined');
    },
    deactivatedFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'deactivated');
    },
    expiredFundraisers() {
      return this.fundraisers.filter((fundraiser) => fundraiser.status.toLowerCase() === 'expired');
    },
  },
});
